import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Pagination,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Factory as FactoryIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  CheckCircle as CompleteIcon,
  Close as CloseIcon,
  Pause as PauseIcon,
  PlayCircle as ContinueIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSearchParams } from 'react-router-dom';
import {
  productionOrderService,
  ProductionOrderWithProduct,
  CreateProductionOrderData,
  ProductionOrderFilters,
  ProductionOrderBulkRequirement,
} from '../services/productionOrderService';
import { productService, Product } from '../services/productService';
import { productionLineService, ProductionLine } from '../services/productionLineService';
import { formatDate, formatTime, formatDateForInput } from '../utils/date';
import { formatApiErrorForUser } from '../utils/apiRequestError';
import {
  actualProductionDurationMinutes,
  formatActualProductionMinutesLabel,
} from '../utils/productionOrderTime';

const ProductionOrders: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [productionOrders, setProductionOrders] = useState<ProductionOrderWithProduct[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productionLines, setProductionLines] = useState<ProductionLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  // URL state management
  const currentPage = parseInt(searchParams.get('page') || '1');
  const currentSearch = searchParams.get('search') || '';
  const selectedOrderId = searchParams.get('orderId')
    ? parseInt(searchParams.get('orderId')!)
    : null;

  // Selected order for detailed view
  const selectedOrder = productionOrders.find(
    (order) => order.production_order_id === selectedOrderId
  );

  // Pagination state
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  // Search with debounce
  const [searchTerm, setSearchTerm] = useState(currentSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(currentSearch);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openStartDialog, setOpenStartDialog] = useState(false);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState<ProductionOrderWithProduct | null>(null);
  const [startingOrder, setStartingOrder] = useState<ProductionOrderWithProduct | null>(null);
  const [completingOrder, setCompletingOrder] = useState<ProductionOrderWithProduct | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateProductionOrderData>({
    product_id: 0,
    quantity_cases: 0,
    quantity_units: 0,
    due_date: '',
    priority_level: 'normal',
    notes: '',
    production_line_id: undefined,
  });

  // Filters
  const [filters, setFilters] = useState<ProductionOrderFilters>({});

  // Start production form data
  const [startFormData, setStartFormData] = useState({
    overrideCases: 0,
  });

  // Complete production form data
  const [completeFormData, setCompleteFormData] = useState({
    actualCasesProduced: 0,
    actualUnitsProduced: 0,
    productionIssues: '',
    issueCategory: '',
    issueSeverity: 'low' as 'low' | 'medium' | 'high',
    bulkUsedNotes: '',
    createNewOrderForLeftover: false,
    newOrderDueDate: '',
    newOrderPriority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    newOrderNotes: '',
  });

  // Bulk requirements and usage
  const [bulkRequirements, setBulkRequirements] = useState<ProductionOrderBulkRequirement[]>([]);
  const [selectedOrderBulkRequirements, setSelectedOrderBulkRequirements] = useState<
    ProductionOrderBulkRequirement[]
  >([]);
  const [bulkUsage, setBulkUsage] = useState<
    Array<{
      bulk_product_id: number;
      bulk_product: {
        name: string;
        sku_number: string;
      };
      cases_needed: number;
      cases_used: number;
      notes: string;
    }>
  >([]);

  // Edit dialog confirmation
  const [showEditConfirmation, setShowEditConfirmation] = useState(false);
  const [pendingEditData, setPendingEditData] = useState<any>(null);

  // Completed order edit form data
  const [completedOrderFormData, setCompletedOrderFormData] = useState({
    actual_start_time: '',
    actual_end_time: '',
    actual_cases_produced: 0,
    actual_units_produced: 0,
  });

  // Ref to track if we've already attempted to find an order to prevent infinite loops
  const orderSearchAttempted = useRef<number | null>(null);
  // Ref to track if we're currently updating URL to prevent loops
  const isUpdatingUrl = useRef(false);

  // View mode
  // const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  // const [calendarOrders, setCalendarOrders] = useState<ProductionOrderWithProduct[]>([]);

  // Track previous debounced search term to detect changes
  const prevDebouncedSearchTerm = useRef(debouncedSearchTerm);
  const prevFilters = useRef<ProductionOrderFilters>(filters);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update URL when search changes (page is managed by handlePageChange, orderId by orderId effect)
  // NOTE: This effect only runs when debouncedSearchTerm changes, NOT when searchParams changes
  // This prevents it from interfering with page or orderId changes
  useEffect(() => {
    // Read current URL params directly (not from searchParams hook to avoid dependency)
    const currentParams = new URLSearchParams(window.location.search);
    // Skip if we're currently updating URL from the orderId effect or handlePageChange
    if (isUpdatingUrl.current) {
      return;
    }

    const searchChanged = prevDebouncedSearchTerm.current !== debouncedSearchTerm;

    // Only update URL if search actually changed
    if (!searchChanged) {
      // Search didn't change - don't touch the URL at all
      // This prevents the effect from interfering when only page or orderId changes
      return;
    }

    // Search changed - update URL, reset page to 1, preserve orderId
    const desiredParams = new URLSearchParams();

    // Set search parameter
    if (debouncedSearchTerm) {
      desiredParams.set('search', debouncedSearchTerm);
    }

    // Reset page to 1 when search changes (page 1 is default, so don't include it)

    // Preserve orderId if it exists
    const currentOrderId = currentParams.get('orderId');
    if (currentOrderId) {
      desiredParams.set('orderId', currentOrderId);
    }
    // Note: We intentionally don't preserve page here - when search changes, we reset to page 1

    // Check if search parameter actually changed in the URL
    const currentSearchInUrl = currentParams.get('search');
    const searchMatches = currentSearchInUrl === (debouncedSearchTerm || null);

    // Only update if search actually changed in the URL
    if (!searchMatches) {
      isUpdatingUrl.current = true;
      setSearchParams(desiredParams, { replace: true });
      setTimeout(() => {
        isUpdatingUrl.current = false;
      }, 200);
    }

    // Update ref for next comparison
    prevDebouncedSearchTerm.current = debouncedSearchTerm;
  }, [debouncedSearchTerm, setSearchParams]); // Removed searchParams from deps - only run when search term changes

  useEffect(() => {
    loadData();
    loadProducts();
    loadProductionLines();
  }, []);

  // Load production lines when create dialog opens
  useEffect(() => {
    if (openCreateDialog && productionLines.length === 0) {
      loadProductionLines();
    }
  }, [openCreateDialog, productionLines.length]);

  // Handle orderId and page logic
  // Step 1: Check if there's an orderId and/or page in URL
  // Step 2: If no orderId, load regularly (handled by loadProductionOrders effect)
  // Step 3: If there's an orderId, find the page for that order
  // Step 4: Load that page and select the order (selectedOrder is computed automatically)
  // Step 5: Ensure URL has the correct page parameter
  useEffect(() => {
    const hasOrderId = selectedOrderId !== null;
    const hasPageParam = searchParams.has('page');

    // Step 2: If no orderId, load regularly (handled by loadProductionOrders effect)
    if (!hasOrderId) {
      orderSearchAttempted.current = null;
      return;
    }

    // Step 3 & 4: If there's an orderId, verify it's on the current page or find the correct page
    // Reset attempt tracking if orderId changes
    if (selectedOrderId !== orderSearchAttempted.current) {
      orderSearchAttempted.current = null;
    }

    // Skip if we've already attempted this orderId or if currently loading
    if (orderSearchAttempted.current === selectedOrderId || loading) {
      return;
    }

    // Step 4: Check if order is already on the current page (if page is specified and orders are loaded)
    if (hasPageParam && productionOrders.length > 0) {
      const isOnCurrentPage = productionOrders.some(
        (o) => o.production_order_id === selectedOrderId
      );
      if (isOnCurrentPage) {
        // Order is on current page - URL is correct, order will be selected automatically
        // selectedOrder is computed from productionOrders.find() above
        // Mark as attempted to prevent re-running
        orderSearchAttempted.current = selectedOrderId;
        return;
      }
    }

    // Prevent infinite loops - if we're already updating, skip
    if (isUpdatingUrl.current) {
      return;
    }

    // Step 3: Need to find the page for this order
    orderSearchAttempted.current = selectedOrderId;

    const findAndLoadOrderPage = async () => {
      try {
        // Verify order exists
        const order = await productionOrderService.getProductionOrderById(selectedOrderId);
        if (!order) {
          // Order doesn't exist, remove orderId from URL
          const params = new URLSearchParams(searchParams);
          params.delete('orderId');
          setSearchParams(params, { replace: true });
          orderSearchAttempted.current = null;
          return;
        }

        // Try searching by ID first (fast path)
        const searchResponse = await productionOrderService.getProductionOrdersPaginated({
          ...filters,
          search: selectedOrderId.toString(),
          page: 1,
          pageSize: pageSize,
        });

        if (searchResponse.data.some((o) => o.production_order_id === selectedOrderId)) {
          // Found via search - update URL with page=1 and search
          // This will trigger loadProductionOrders which loads page 1 with search
          // selectedOrder will be automatically set when orders load
          isUpdatingUrl.current = true;
          const params = new URLSearchParams(searchParams);
          params.set('page', '1');
          params.set('search', selectedOrderId.toString());
          params.set('orderId', selectedOrderId.toString());
          setSearchParams(params, { replace: true });
          // Reset flag after a short delay to allow URL to update
          setTimeout(() => {
            isUpdatingUrl.current = false;
          }, 100);
          return;
        }

        // Search through pages to find the order
        let foundPage = null;
        const maxPagesToSearch = 100;

        for (let page = 1; page <= maxPagesToSearch; page++) {
          const response = await productionOrderService.getProductionOrdersPaginated({
            ...filters,
            page: page,
            pageSize: pageSize,
          });

          if (response.data.some((o) => o.production_order_id === selectedOrderId)) {
            foundPage = page;
            break;
          }

          if (page >= response.pagination.totalPages) {
            break;
          }
        }

        // Step 5: Update URL with correct page
        // This will trigger loadProductionOrders which loads the correct page
        // selectedOrder will be automatically set when orders load
        isUpdatingUrl.current = true;
        const params = new URLSearchParams(searchParams);
        if (foundPage) {
          params.set('page', foundPage.toString());
        } else {
          params.set('page', '1'); // Fallback to page 1 if not found
        }
        params.set('orderId', selectedOrderId.toString());
        // Preserve existing search if it exists
        const existingSearch = searchParams.get('search');
        if (existingSearch) params.set('search', existingSearch);
        setSearchParams(params, { replace: true });
        // Reset flag after a short delay to allow URL to update
        setTimeout(() => {
          isUpdatingUrl.current = false;
        }, 100);
      } catch (err) {
        console.error('Error finding order page:', err);
        orderSearchAttempted.current = null;
      }
    };

    findAndLoadOrderPage();
  }, [
    selectedOrderId,
    currentPage,
    productionOrders.length, // Only depend on length, not the array itself
    loading,
    filters,
    pageSize,
    searchParams,
    setSearchParams,
  ]);

  // Reset page to 1 when filters change (but not when page changes)
  // NOTE: This effect only runs when filters.status or filters.priority change, NOT when searchParams changes
  // This prevents it from interfering with page changes
  useEffect(() => {
    // Check if filters actually changed (compare values, not references)
    // Handle undefined/null cases: undefined !== undefined should be false, but undefined !== 'pending' should be true
    const prevStatus = prevFilters.current.status ?? null;
    const currentStatus = filters.status ?? null;
    const prevPriority = prevFilters.current.priority ?? null;
    const currentPriority = filters.priority ?? null;

    const statusChanged = prevStatus !== currentStatus;
    const priorityChanged = prevPriority !== currentPriority;
    const filtersChanged = statusChanged || priorityChanged;

    // Update ref for next comparison (create a copy, not a reference)
    prevFilters.current = { status: filters.status, priority: filters.priority };

    // Only proceed if filters actually changed
    if (!filtersChanged) {
      return;
    }

    // Skip if we're currently updating URL
    if (isUpdatingUrl.current) {
      return;
    }
    // Only reset page when filters actually change, not when searchParams changes
    // Read current URL directly (not from searchParams hook to avoid dependency)
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') && params.get('page') !== '1') {
      params.delete('page');
      setSearchParams(params, { replace: true });
    }
  }, [filters.status, filters.priority, setSearchParams]); // Removed searchParams from deps - only run when filters change

  useEffect(() => {
    // Note: We don't skip when isUpdatingUrl is true because this effect depends on searchParams,
    // and when searchParams changes, we want to load data regardless of the isUpdatingUrl flag.
    // The isUpdatingUrl flag is only used to prevent the search effect from interfering with page/orderId changes.
    // loadProductionOrders reads the page directly from searchParams to avoid stale closure issues
    loadProductionOrders();
  }, [searchParams, debouncedSearchTerm, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      await loadProductionOrders();
    } catch (err) {
      setError('Failed to load production orders');
      console.error('Error loading production orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProductionOrders = async () => {
    // Read page directly from searchParams to avoid stale closure issues
    const pageFromUrl = parseInt(searchParams.get('page') || '1');
    try {
      const response = await productionOrderService.getProductionOrdersPaginated({
        ...filters,
        search: debouncedSearchTerm || undefined,
        page: pageFromUrl,
        pageSize: pageSize,
      });

      setProductionOrders(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.total);

      // Clamp current page to valid range if it's beyond available pages
      // Only update URL if page actually needs to be clamped to avoid flickering
      const validTotalPages = Math.max(1, response.pagination.totalPages);
      if (pageFromUrl > validTotalPages) {
        const params = new URLSearchParams(searchParams);
        const currentPageInUrl = params.get('page');
        const targetPage = validTotalPages === 1 ? null : validTotalPages.toString();

        // Only update if the URL page doesn't match what we want
        if (currentPageInUrl !== targetPage) {
          if (validTotalPages === 1) {
            params.delete('page');
          } else {
            params.set('page', validTotalPages.toString());
          }
          setSearchParams(params, { replace: true });
        }
      }
    } catch (err) {
      console.error('Error loading production orders:', err);
    }
  };

  const loadProductionLines = async () => {
    try {
      const lines = await productionLineService.getActiveProductionLines();
      setProductionLines(lines);
    } catch (err) {
      console.error('Error loading production lines:', err);
    }
  };

  const loadProducts = async (searchTerm?: string, preserveSelectedId?: number) => {
    setLoadingProducts(true);
    try {
      let loadedProducts: Product[] = [];
      if (searchTerm && searchTerm.trim()) {
        // Only show production products (includeAll defaults to false)
        const response = await productService.searchProductsPaginated(
          searchTerm,
          1,
          50,
          false,
          false
        );
        loadedProducts = response.products as Product[];
      } else {
        // Only show production products (includeAll defaults to false)
        const response = await productService.getProductsPaginated(1, 50, false, false);
        loadedProducts = response.products as Product[];
      }

      // If we have a selected product ID and it's not in the loaded products, preserve it
      if (preserveSelectedId && !loadedProducts.find((p) => p.id === preserveSelectedId)) {
        // Use functional update to access current products state
        setProducts((currentProducts) => {
          const currentSelected = currentProducts.find((p) => p.id === preserveSelectedId);
          if (currentSelected) {
            // Add selected product to the beginning of the list
            return [currentSelected, ...loadedProducts];
          }
          return loadedProducts;
        });
      } else {
        setProducts(loadedProducts);
      }
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Debounce product search
  useEffect(() => {
    const timer = setTimeout(() => {
      // Preserve selected product when reloading
      loadProducts(productSearchTerm, formData.product_id || undefined);
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  // const loadCalendarView = async () => {
  //   try {
  //     const startDate = new Date();
  //     const endDate = new Date();
  //     endDate.setDate(startDate.getDate() + 30);

  //     const calendarData = await productionOrderService.getCalendarView(
  //       startDate.toISOString().split('T')[0],
  //       endDate.toISOString().split('T')[0]
  //     );
  //     setCalendarOrders(calendarData);
  //   } catch (err) {
  //     console.error('Error loading calendar view:', err);
  //   }
  // };

  // const handleViewModeChange = async (mode: 'list' | 'calendar') => {
  //   setViewMode(mode);
  //   if (mode === 'calendar') {
  //     await loadCalendarView();
  //   }
  // };

  const handleOrderSelect = async (orderId: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('orderId', orderId.toString());
    setSearchParams(params);

    // Load bulk requirements for selected order
    try {
      const requirements = await productionOrderService.getBulkRequirements(orderId);
      setSelectedOrderBulkRequirements(requirements);
    } catch (err) {
      console.error('Error loading bulk requirements for selected order:', err);
      setSelectedOrderBulkRequirements([]);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    // Set flag to prevent search/page effect from interfering
    isUpdatingUrl.current = true;

    const params = new URLSearchParams(searchParams);
    if (page > 1) {
      params.set('page', page.toString());
    } else {
      params.delete('page');
    }
    setSearchParams(params);

    // Reset flag after a longer delay to allow URL to update and effects to settle
    setTimeout(() => {
      isUpdatingUrl.current = false;
    }, 200);
  };

  const handleSearchChange = (value: string) => {
    // Only update state - let the debounced effect handle URL updates
    setSearchTerm(value);
  };

  const handleCreateOrder = async () => {
    // Prevent multiple submissions
    if (isCreatingOrder) {
      return;
    }

    try {
      if (!formData.product_id || formData.product_id === 0) {
        setError('Please select a product');
        return;
      }

      if (formData.quantity_cases === 0 && formData.quantity_units === 0) {
        setError('Please enter a quantity');
        return;
      }

      setIsCreatingOrder(true);
      setDialogError(null);

      await productionOrderService.createProductionOrder({
        ...formData,
        due_date: formData.due_date || undefined,
      });

      setSuccess('Production order created successfully');
      setOpenCreateDialog(false);
      resetForm();
      loadData();
    } catch (err: unknown) {
      const msg = formatApiErrorForUser(err) || 'Failed to create production order';
      setError(msg);
      setDialogError(msg);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleUpdateOrder = () => {
    if (!editingOrder) return;

    if (editingOrder.status === 'completed') {
      // For completed orders, store the completed order data
      setPendingEditData({
        actual_start_time: completedOrderFormData.actual_start_time || undefined,
        actual_end_time: completedOrderFormData.actual_end_time || undefined,
        actual_cases_produced: completedOrderFormData.actual_cases_produced,
        actual_units_produced: completedOrderFormData.actual_units_produced,
      });
    } else {
      // For pending orders, store the regular form data
      setPendingEditData({
        quantity_cases: formData.quantity_cases,
        quantity_units: formData.quantity_units,
        due_date: formData.due_date || undefined,
        priority_level: formData.priority_level,
        notes: formData.notes,
        production_line_id: formData.production_line_id || undefined,
      });
    }
    setShowEditConfirmation(true);
  };

  const handleConfirmUpdate = async () => {
    if (!editingOrder || !pendingEditData) return;

    try {
      if (editingOrder.status === 'completed') {
        // Convert datetime-local format to ISO string for backend
        const updateData: any = {
          actual_cases_produced: pendingEditData.actual_cases_produced,
          actual_units_produced: pendingEditData.actual_units_produced,
        };

        if (pendingEditData.actual_start_time) {
          // Convert datetime-local (YYYY-MM-DDTHH:mm) to ISO string
          const startDate = new Date(pendingEditData.actual_start_time);
          if (!isNaN(startDate.getTime())) {
            updateData.actual_start_time = startDate.toISOString();
          }
        }

        if (pendingEditData.actual_end_time) {
          // Convert datetime-local (YYYY-MM-DDTHH:mm) to ISO string
          const endDate = new Date(pendingEditData.actual_end_time);
          if (!isNaN(endDate.getTime())) {
            updateData.actual_end_time = endDate.toISOString();
          }
        }

        // Use the completed order update endpoint
        await productionOrderService.updateCompletedProductionOrder(
          editingOrder.production_order_id,
          updateData
        );
      } else {
        // Use the regular update endpoint
        await productionOrderService.updateProductionOrder(
          editingOrder.production_order_id,
          pendingEditData
        );
      }

      setSuccess('Production order updated successfully');
      setOpenEditDialog(false);
      setShowEditConfirmation(false);
      setPendingEditData(null);
      resetForm();
      loadData();
    } catch (err: unknown) {
      setError(formatApiErrorForUser(err) || 'Failed to update production order');
      setShowEditConfirmation(false);
    }
  };

  const handleCancelUpdate = () => {
    setShowEditConfirmation(false);
    setPendingEditData(null);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setShowEditConfirmation(false);
    setPendingEditData(null);
    resetForm();
  };

  // Enhanced start production handler
  const handleStartProduction = async (order: ProductionOrderWithProduct) => {
    setStartingOrder(order);
    setStartFormData({
      overrideCases: order.quantity_cases,
    });

    // Load bulk requirements
    try {
      const requirements = await productionOrderService.getBulkRequirements(
        order.production_order_id
      );
      setBulkRequirements(requirements);
    } catch (err) {
      console.error('Error loading bulk requirements:', err);
    }

    setOpenStartDialog(true);
  };

  const handleConfirmStartProduction = async () => {
    if (!startingOrder) return;

    try {
      const overrideCases =
        startFormData.overrideCases !== startingOrder.quantity_cases
          ? startFormData.overrideCases
          : undefined;

      await productionOrderService.startProductionOrder(
        startingOrder.production_order_id,
        overrideCases
      );

      setSuccess(`Production order #${startingOrder.production_order_id} started successfully`);
      setOpenStartDialog(false);
      setStartingOrder(null);
      loadData();
    } catch (err: unknown) {
      setError(formatApiErrorForUser(err) || 'Failed to start production order');
    }
  };

  // Enhanced complete production handler
  const handleCompleteProduction = async (order: ProductionOrderWithProduct) => {
    setCompletingOrder(order);

    // Initialize form with calculated values
    setCompleteFormData({
      actualCasesProduced: order.quantity_cases,
      actualUnitsProduced: order.quantity_units,
      productionIssues: '',
      issueCategory: '',
      issueSeverity: 'low',
      bulkUsedNotes: '',
      createNewOrderForLeftover: false,
      newOrderDueDate: '',
      newOrderPriority: order.priority_level,
      newOrderNotes: '',
    });

    // Load bulk requirements and calculate expected usage
    try {
      const requirements = await productionOrderService.getBulkRequirements(
        order.production_order_id
      );
      setBulkRequirements(requirements);

      // Initialize bulk usage with calculated values
      const calculatedUsage = requirements.map((req) => ({
        bulk_product_id: req.bulk_product_id,
        bulk_product: {
          name: req.bulk_product.name,
          sku_number: req.bulk_product.sku_number,
        },
        cases_needed: req.cases_needed,
        cases_used: req.cases_needed, // Default to expected usage
        notes: '',
      }));
      setBulkUsage(calculatedUsage);
    } catch (err) {
      console.error('Error loading bulk requirements:', err);
    }

    setOpenCompleteDialog(true);
  };

  const handleConfirmCompleteProduction = async () => {
    if (!completingOrder) return;

    try {
      const completionData = {
        actualCasesProduced: completeFormData.actualCasesProduced,
        actualUnitsProduced: completeFormData.actualUnitsProduced,
        productionIssues: completeFormData.productionIssues || undefined,
        bulkUsedNotes: completeFormData.bulkUsedNotes || undefined,
        bulkUsage: bulkUsage.map((usage) => ({
          bulk_product_id: usage.bulk_product_id,
          cases_used: usage.cases_used,
          notes: usage.notes || undefined,
        })),
        createNewOrderForLeftover: completeFormData.createNewOrderForLeftover,
        newOrderDueDate: completeFormData.newOrderDueDate || undefined,
      };

      const result = await productionOrderService.completeProductionOrder(
        completingOrder.production_order_id,
        completionData
      );

      let successMessage = `Production order #${completingOrder.production_order_id} completed successfully`;
      if (result.newOrderId) {
        successMessage += `. New order #${result.newOrderId} created for leftover cases.`;
      }

      setSuccess(successMessage);
      setOpenCompleteDialog(false);
      setCompletingOrder(null);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to complete production order');
    }
  };

  const handleStatusAction = async (orderId: number, action: 'start' | 'stop') => {
    const order = productionOrders.find((o) => o.production_order_id === orderId);
    if (!order) return;

    if (action === 'start') {
      handleStartProduction(order);
    } else {
      handleCompleteProduction(order);
    }
  };

  const handlePauseProductionOrder = async (order: ProductionOrderWithProduct) => {
    try {
      const reason = window.prompt('Pause reason (optional):') || undefined;
      await productionOrderService.pauseProductionOrder(order.production_order_id, reason);
      setSuccess(`Production order #${order.production_order_id} paused`);
      await loadData();
    } catch (err: unknown) {
      setError(formatApiErrorForUser(err) || 'Failed to pause production order');
    }
  };

  const handleContinueProductionOrder = async (order: ProductionOrderWithProduct) => {
    try {
      await productionOrderService.continueProductionOrder(order.production_order_id);
      setSuccess(`Production order #${order.production_order_id} resumed`);
      await loadData();
    } catch (err: unknown) {
      setError(formatApiErrorForUser(err) || 'Failed to continue production order');
    }
  };

  const handleDeleteOrder = async (orderId: number) => {
    if (!window.confirm('Are you sure you want to cancel this production order?')) {
      return;
    }

    try {
      await productionOrderService.deleteProductionOrder(orderId);
      setSuccess('Production order cancelled successfully');
      loadData();
      // Clear selection if deleted order was selected
      if (selectedOrderId === orderId) {
        const params = new URLSearchParams(searchParams);
        params.delete('orderId');
        setSearchParams(params);
      }
    } catch (err: unknown) {
      setError(formatApiErrorForUser(err) || 'Failed to cancel production order');
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: 0,
      quantity_cases: 0,
      quantity_units: 0,
      due_date: '',
      priority_level: 'normal',
      notes: '',
      production_line_id: undefined,
    });
    setEditingOrder(null);
    setProductSearchTerm('');
    setDialogError(null);
  };

  // Helper function to format datetime for datetime-local input
  const formatDateTimeForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      // Format as YYYY-MM-DDTHH:mm for datetime-local input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Handle date field change with validation
  const handleDateChange = (fieldName: 'due_date', newValue: string) => {
    // Clear dialog error when user starts typing
    setDialogError(null);

    // Allow empty values
    if (!newValue) {
      setFormData({ ...formData, [fieldName]: '' });
      return;
    }

    // Always update the value so user can see what they typed
    setFormData({ ...formData, [fieldName]: newValue });

    // Check if it matches the expected YYYY-MM-DD format (complete date)
    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = newValue.match(dateRegex);

    if (match) {
      // Complete date format - validate strictly
      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const day = parseInt(match[3], 10);

      // Validate year is exactly 4 digits (1000-9999)
      if (isNaN(year) || year < 1000 || year > 9999 || match[1].length !== 4) {
        setDialogError(`Invalid year. Please use a 4-digit year between 1000 and 9999.`);
        return;
      }

      // Validate month is 01-12
      if (isNaN(month) || month < 1 || month > 12) {
        setDialogError(`Invalid month. Please enter a month between 01 and 12.`);
        return;
      }

      // Validate day is reasonable (01-31, will be further validated by Date object)
      if (isNaN(day) || day < 1 || day > 31) {
        setDialogError(`Invalid day. Please enter a valid day.`);
        return;
      }

      // Validate the date is actually valid (e.g., not Feb 30)
      const date = new Date(newValue + 'T00:00:00');
      if (isNaN(date.getTime())) {
        setDialogError(`Invalid date. Please enter a valid date.`);
        return;
      }

      // Ensure the date string matches what the Date object would produce
      const yearStr = date.getFullYear().toString();
      const monthStr = String(date.getMonth() + 1).padStart(2, '0');
      const dayStr = String(date.getDate()).padStart(2, '0');
      const normalizedDate = `${yearStr}-${monthStr}-${dayStr}`;

      if (normalizedDate !== newValue) {
        setDialogError(`Invalid date. Please enter a valid date.`);
        return;
      }

      // Date is valid, clear any error
      setDialogError(null);
    } else {
      // Partial input - be more lenient, only reject clearly invalid cases
      // Check if there are more than 4 consecutive digits at the start (invalid year like 122312, 275760)
      const invalidYearMatch = newValue.match(/^(\d{5,})/);
      if (invalidYearMatch) {
        // More than 4 digits at start without dashes - show error but keep value
        setDialogError(`Invalid year. Please use a 4-digit year between 1000 and 9999.`);
        return;
      }

      // Check if year part (before first dash or end of string) is invalid
      const yearPartMatch = newValue.match(/^(\d+)(?:-|$)/);
      if (yearPartMatch) {
        const yearStr = yearPartMatch[1];
        // Only validate if we have a complete 4-digit year
        if (yearStr.length === 4) {
          const year = parseInt(yearStr, 10);
          if (isNaN(year) || year < 1000 || year > 9999) {
            // Invalid 4-digit year - show error but keep value
            setDialogError(`Invalid year. Please use a 4-digit year between 1000 and 9999.`);
            return;
          }
        }
        // Show error if year part has more than 4 digits
        if (yearStr.length > 4) {
          setDialogError(`Invalid year. Please use a 4-digit year between 1000 and 9999.`);
          return;
        }
      }

      // Partial input is valid, clear error
      setDialogError(null);
    }
  };

  // Handle date field blur with validation (fallback)
  const handleDateBlur = (fieldName: 'due_date', currentValue: string) => {
    // Don't clear the value - let user see what they typed
    // Just validate and show error if invalid
    if (!currentValue) {
      return; // Empty is allowed
    }

    // Check if it matches the expected YYYY-MM-DD format (complete date)
    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = currentValue.match(dateRegex);

    if (match) {
      // Complete date format - validate strictly
      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const day = parseInt(match[3], 10);

      // Validate year is exactly 4 digits (1000-9999)
      if (isNaN(year) || year < 1000 || year > 9999 || match[1].length !== 4) {
        setDialogError(`Invalid year. Please use a 4-digit year between 1000 and 9999.`);
        return;
      }

      // Validate month is 01-12
      if (isNaN(month) || month < 1 || month > 12) {
        setDialogError(`Invalid month. Please enter a month between 01 and 12.`);
        return;
      }

      // Validate day is reasonable (01-31, will be further validated by Date object)
      if (isNaN(day) || day < 1 || day > 31) {
        setDialogError(`Invalid day. Please enter a valid day.`);
        return;
      }

      // Validate the date is actually valid (e.g., not Feb 30)
      const date = new Date(currentValue + 'T00:00:00');
      if (isNaN(date.getTime())) {
        setDialogError(`Invalid date. Please enter a valid date.`);
        return;
      }

      // Ensure the date string matches what the Date object would produce
      const yearStr = date.getFullYear().toString();
      const monthStr = String(date.getMonth() + 1).padStart(2, '0');
      const dayStr = String(date.getDate()).padStart(2, '0');
      const normalizedDate = `${yearStr}-${monthStr}-${dayStr}`;

      if (normalizedDate !== currentValue) {
        setDialogError(`Invalid date. Please enter a valid date.`);
        return;
      }

      // Date is valid, clear any error
      setDialogError(null);
    } else {
      // Not a complete date - check if it's clearly invalid
      const invalidYearMatch = currentValue.match(/^(\d{5,})/);
      if (invalidYearMatch) {
        setDialogError(`Invalid year. Please use a 4-digit year between 1000 and 9999.`);
        return;
      }

      // Check if year part is invalid
      const yearPartMatch = currentValue.match(/^(\d+)(?:-|$)/);
      if (yearPartMatch) {
        const yearStr = yearPartMatch[1];
        if (yearStr.length === 4) {
          const year = parseInt(yearStr, 10);
          if (isNaN(year) || year < 1000 || year > 9999) {
            setDialogError(`Invalid year. Please use a 4-digit year between 1000 and 9999.`);
            return;
          }
        }
        if (yearStr.length > 4) {
          setDialogError(`Invalid year. Please use a 4-digit year between 1000 and 9999.`);
          return;
        }
      }

      // Partial input - show error if it's not empty
      if (currentValue.trim()) {
        setDialogError(`Invalid date format. Please use YYYY-MM-DD format with a 4-digit year.`);
      }
    }
  };

  const openEditForm = async (order: ProductionOrderWithProduct) => {
    setEditingOrder(order);
    // Ensure production lines are loaded
    if (productionLines.length === 0) {
      await loadProductionLines();
    }
    if (order.status === 'completed') {
      // For completed orders, populate the completed order form
      setCompletedOrderFormData({
        actual_start_time: formatDateTimeForInput(order.actual_start_time),
        actual_end_time: formatDateTimeForInput(order.actual_end_time),
        actual_cases_produced: order.actual_cases_produced || 0,
        actual_units_produced: order.actual_units_produced || 0,
      });
    } else {
      // For pending orders, use the regular form
      setFormData({
        product_id: order.product_id,
        quantity_cases: order.quantity_cases,
        quantity_units: order.quantity_units,
        due_date: formatDateForInput(order.due_date),
        priority_level: order.priority_level,
        notes: order.notes || '',
        production_line_id: order.production_line_id || undefined,
      });
    }
    setOpenEditDialog(true);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'success';
      case 'normal':
        return 'default';
      case 'high':
        return 'warning';
      case 'urgent':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatQuantity = (cases: number, units: number) => {
    if (cases > 0 && units > 0) {
      return `${cases} cases + ${units} units`;
    } else if (cases > 0) {
      return `${cases} cases`;
    } else if (units > 0) {
      return `${units} units`;
    }
    return '0';
  };

  // Orders are already paginated from the API
  const paginatedOrders = productionOrders;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <FactoryIcon />
            Production Orders
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateDialog(true)}
              sx={{ bgcolor: '#1976d2' }}
            >
              Create Order
            </Button>
          </Stack>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Search and Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search orders..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by product name, order ID..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filters.priority || ''}
                  onChange={(e) =>
                    setFilters({ ...filters, priority: e.target.value || undefined })
                  }
                  label="Priority"
                >
                  <MenuItem value="">All Priorities</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} fullWidth>
                Refresh
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box textAlign="right">
                <Typography variant="body2" color="text.secondary">
                  Page {currentPage} of {totalPages}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalItems)}{' '}
                  of {totalItems} orders
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Main Content */}
        <Box sx={{ height: 'calc(100vh - 300px)', display: 'flex', gap: 3 }}>
          {/* Left Side - Orders List */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                <Typography variant="h6">Production Orders</Typography>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {paginatedOrders.map((order) => (
                  <Card
                    key={order.production_order_id}
                    sx={{
                      mb: 2,
                      cursor: 'pointer',
                      border:
                        selectedOrderId === order.production_order_id
                          ? '2px solid #1976d2'
                          : '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: 3,
                        borderColor: '#1976d2',
                      },
                    }}
                    onClick={() => handleOrderSelect(order.production_order_id)}
                  >
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                      >
                        <Box>
                          <Typography variant="h6" component="h3">
                            #{order.production_order_id} - {order.product_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {order.product_code}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={
                              Boolean((order as any).is_paused) && order.status === 'in_progress'
                                ? 'IN PROGRESS (PAUSED)'
                                : order.status.replace('_', ' ').toUpperCase()
                            }
                            color={
                              Boolean((order as any).is_paused) && order.status === 'in_progress'
                                ? 'warning'
                                : (getStatusColor(order.status) as any)
                            }
                            size="small"
                          />
                          <Chip
                            label={order.priority_level.toUpperCase()}
                            color={getPriorityColor(order.priority_level) as any}
                            size="small"
                          />
                        </Stack>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Quantity
                          </Typography>
                          <Typography variant="body1">
                            {formatQuantity(order.quantity_cases, order.quantity_units)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Due Date
                          </Typography>
                          <Typography variant="body1">
                            {order.due_date ? formatDate(order.due_date) : 'Not set'}
                          </Typography>
                        </Grid>
                        {order.planned_production_date && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Planned Production
                            </Typography>
                            <Typography variant="body1">
                              {formatDate(order.planned_production_date)}
                              {order.planned_production_time &&
                                ` at ${formatTime(order.planned_production_time)}`}
                            </Typography>
                          </Grid>
                        )}
                        {/* Show start time for in-progress and completed orders */}
                        {(order.status === 'in_progress' || order.status === 'completed') &&
                          order.actual_start_time && (
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Started
                              </Typography>
                              <Typography variant="body1">
                                {new Date(order.actual_start_time).toLocaleString()}
                              </Typography>
                            </Grid>
                          )}
                        {/* Show end time for completed orders */}
                        {order.status === 'completed' && order.actual_end_time && (
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Completed
                            </Typography>
                            <Typography variant="body1">
                              {new Date(order.actual_end_time).toLocaleString()}
                            </Typography>
                          </Grid>
                        )}
                        {/* Show lot number for started orders */}
                        {(order.status === 'in_progress' || order.status === 'completed') &&
                          order.batch_number && (
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Lot Number
                              </Typography>
                              <Typography variant="body1">{order.batch_number}</Typography>
                            </Grid>
                          )}
                        {/* Show actual production for completed orders */}
                        {order.status === 'completed' &&
                          (order.actual_cases_produced !== null ||
                            order.actual_units_produced !== null) && (
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Actual Production
                              </Typography>
                              <Typography variant="body1">
                                {formatQuantity(
                                  order.actual_cases_produced || 0,
                                  order.actual_units_produced || 0
                                )}
                              </Typography>
                            </Grid>
                          )}
                      </Grid>
                    </CardContent>
                  </Card>
                ))}

                {paginatedOrders.length === 0 && (
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <FactoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Production Orders Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {debouncedSearchTerm
                        ? `No orders match "${debouncedSearchTerm}"`
                        : 'No production orders available'}
                    </Typography>
                  </Paper>
                )}

                {totalPages > 1 && (
                  <Box display="flex" flexDirection="column" alignItems="center" mt={3} gap={1}>
                    <Box textAlign="center">
                      <Typography variant="body2" color="text.secondary">
                        Page {currentPage} of {totalPages}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(currentPage - 1) * pageSize + 1}-
                        {Math.min(currentPage * pageSize, totalItems)} of {totalItems} orders
                      </Typography>
                    </Box>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>

          {/* Right Side - Order Details */}
          <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                <Typography variant="h6">Order Details</Typography>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                {selectedOrder ? (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Order Details</Typography>
                      <Stack direction="row" spacing={1}>
                        {selectedOrder.status === 'pending' && (
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<StartIcon />}
                            onClick={() =>
                              handleStatusAction(selectedOrder.production_order_id, 'start')
                            }
                          >
                            Start
                          </Button>
                        )}
                        {selectedOrder.status === 'in_progress' && (
                          <>
                            {(selectedOrder as { is_paused?: boolean }).is_paused ? (
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<ContinueIcon />}
                                onClick={() => handleContinueProductionOrder(selectedOrder)}
                              >
                                Continue
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                startIcon={<PauseIcon />}
                                onClick={() => handlePauseProductionOrder(selectedOrder)}
                              >
                                Pause
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              startIcon={<StopIcon />}
                              onClick={() =>
                                handleStatusAction(selectedOrder.production_order_id, 'stop')
                              }
                            >
                              Complete
                            </Button>
                          </>
                        )}
                        {selectedOrder.status === 'pending' ||
                        selectedOrder.status === 'completed' ? (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => openEditForm(selectedOrder)}
                          >
                            Edit
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            disabled
                            title={`Cannot edit ${selectedOrder.status} orders. Only pending and completed orders can be edited.`}
                          >
                            Edit
                          </Button>
                        )}
                        {selectedOrder.status === 'pending' && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteOrder(selectedOrder.production_order_id)}
                          >
                            Cancel
                          </Button>
                        )}
                      </Stack>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Order ID"
                          secondary={`#${selectedOrder.production_order_id}`}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Product"
                          secondary={`${selectedOrder.product_name} (${selectedOrder.product_code})`}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Quantity"
                          secondary={formatQuantity(
                            selectedOrder.quantity_cases,
                            selectedOrder.quantity_units
                          )}
                        />
                      </ListItem>
                      <Divider />
                      {selectedOrder.production_line_name && (
                        <>
                          <ListItem>
                            <ListItemText
                              primary="Production Line"
                              secondary={selectedOrder.production_line_name}
                            />
                          </ListItem>
                          <Divider />
                        </>
                      )}
                      <ListItem>
                        <ListItemText
                          primary="Status"
                          secondary={
                            <Chip
                              label={
                                Boolean((selectedOrder as any).is_paused) &&
                                selectedOrder.status === 'in_progress'
                                  ? 'IN PROGRESS (PAUSED)'
                                  : selectedOrder.status.replace('_', ' ').toUpperCase()
                              }
                              color={
                                Boolean((selectedOrder as any).is_paused) &&
                                selectedOrder.status === 'in_progress'
                                  ? 'warning'
                                  : (getStatusColor(selectedOrder.status) as any)
                              }
                              size="small"
                            />
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Priority"
                          secondary={
                            <Chip
                              label={selectedOrder.priority_level.toUpperCase()}
                              color={getPriorityColor(selectedOrder.priority_level) as any}
                              size="small"
                            />
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                      </ListItem>
                      <Divider />
                      {selectedOrder.due_date && (
                        <>
                          <ListItem>
                            <ListItemText
                              primary="Due Date"
                              secondary={formatDate(selectedOrder.due_date)}
                            />
                          </ListItem>
                          <Divider />
                        </>
                      )}
                      {(selectedOrder as any).current_portion_number && (
                        <>
                          <ListItem>
                            <ListItemText
                              primary="Current Portion"
                              secondary={`Portion ${(selectedOrder as any).current_portion_number}${
                                (selectedOrder as any).current_portion_scheduled_date &&
                                (selectedOrder as any).current_portion_scheduled_time
                                  ? ` - ${formatDate((selectedOrder as any).current_portion_scheduled_date)} ${formatTime((selectedOrder as any).current_portion_scheduled_time)}`
                                  : (selectedOrder as any).current_portion_scheduled_date
                                    ? ` - ${formatDate((selectedOrder as any).current_portion_scheduled_date)}`
                                    : ''
                              }`}
                            />
                          </ListItem>
                          <Divider />
                        </>
                      )}
                      {(selectedOrder as any).scheduled_dates && (
                        <>
                          <ListItem>
                            <ListItemText
                              primary="Scheduled Dates"
                              secondary={
                                (selectedOrder as any).scheduled_dates_times
                                  ? ((selectedOrder as any).scheduled_dates_times as string)
                                      .split(',')
                                      .map((pair: string) => {
                                        const [date, time] = pair.trim().split('|');
                                        return time
                                          ? `${formatDate(date)} ${formatTime(time)}`
                                          : formatDate(date);
                                      })
                                      .join(', ')
                                  : ((selectedOrder as any).scheduled_dates as string)
                                      .split(',')
                                      .map((date: string) => formatDate(date.trim()))
                                      .join(', ')
                              }
                            />
                          </ListItem>
                          <Divider />
                        </>
                      )}
                      {selectedOrder.planned_production_date &&
                        !(selectedOrder as any).scheduled_dates && (
                          <>
                            <ListItem>
                              <ListItemText
                                primary="Planned Production"
                                secondary={`${formatDate(selectedOrder.planned_production_date)}${
                                  selectedOrder.planned_production_time
                                    ? ` at ${formatTime(selectedOrder.planned_production_time)}`
                                    : ''
                                }`}
                              />
                            </ListItem>
                            <Divider />
                          </>
                        )}
                      {selectedOrder.estimated_production_hours && (
                        <>
                          <ListItem>
                            <ListItemText
                              primary="Estimated Hours"
                              secondary={`${selectedOrder.estimated_production_hours} hours`}
                            />
                          </ListItem>
                          <Divider />
                        </>
                      )}

                      {/* Production Timeline Section */}
                      {(selectedOrder.status === 'in_progress' ||
                        selectedOrder.status === 'completed') && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" gutterBottom sx={{ px: 2 }}>
                            Production Timeline
                          </Typography>
                          {selectedOrder.batch_number && (
                            <>
                              <ListItem>
                                <ListItemText
                                  primary="Lot Number"
                                  secondary={selectedOrder.batch_number}
                                />
                              </ListItem>
                              <Divider />
                            </>
                          )}
                          {selectedOrder.actual_start_time && (
                            <>
                              <ListItem>
                                <ListItemText
                                  primary="Started"
                                  secondary={new Date(
                                    selectedOrder.actual_start_time
                                  ).toLocaleString()}
                                />
                              </ListItem>
                              <Divider />
                            </>
                          )}
                          {selectedOrder.actual_end_time && (
                            <>
                              <ListItem>
                                <ListItemText
                                  primary="Completed"
                                  secondary={new Date(
                                    selectedOrder.actual_end_time
                                  ).toLocaleString()}
                                />
                              </ListItem>
                              <Divider />
                            </>
                          )}
                          {selectedOrder.status === 'completed' &&
                            selectedOrder.actual_start_time &&
                            selectedOrder.actual_end_time && (
                              <>
                                <ListItem>
                                  <ListItemText
                                    primary="Total Production Time"
                                    secondary={(() => {
                                      const start = new Date(selectedOrder.actual_start_time!);
                                      const end = new Date(selectedOrder.actual_end_time!);
                                      const diffMs = end.getTime() - start.getTime();
                                      const diffHours =
                                        Math.round((diffMs / (1000 * 60 * 60)) * 100) / 100;
                                      return `${diffHours} hours`;
                                    })()}
                                  />
                                </ListItem>
                                <Divider />
                              </>
                            )}
                        </>
                      )}

                      {/* Production Results Section */}
                      {selectedOrder.status === 'completed' && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" gutterBottom sx={{ px: 2 }}>
                            Production Results
                          </Typography>
                          {(selectedOrder.actual_cases_produced !== null ||
                            selectedOrder.actual_units_produced !== null) && (
                            <>
                              <ListItem>
                                <ListItemText
                                  primary="Actual Production"
                                  secondary={formatQuantity(
                                    selectedOrder.actual_cases_produced || 0,
                                    selectedOrder.actual_units_produced || 0
                                  )}
                                />
                              </ListItem>
                              <Divider />
                            </>
                          )}
                          {selectedOrder.actual_cases_produced !== selectedOrder.quantity_cases && (
                            <>
                              <ListItem>
                                <ListItemText
                                  primary="Variance"
                                  secondary={(() => {
                                    const variance =
                                      (selectedOrder.actual_cases_produced || 0) -
                                      selectedOrder.quantity_cases;
                                    const color = variance >= 0 ? 'success.main' : 'error.main';
                                    return (
                                      <Typography component="span" sx={{ color }}>
                                        {variance > 0 ? '+' : ''}
                                        {variance} cases
                                      </Typography>
                                    );
                                  })()}
                                />
                              </ListItem>
                              <Divider />
                            </>
                          )}
                          {selectedOrder.production_issues && (
                            <>
                              <ListItem>
                                <ListItemText
                                  primary="Production Issues"
                                  secondary={selectedOrder.production_issues}
                                />
                              </ListItem>
                              <Divider />
                            </>
                          )}
                          {selectedOrder.bulk_used_notes && (
                            <>
                              <ListItem>
                                <ListItemText
                                  primary="Bulk Usage Notes"
                                  secondary={selectedOrder.bulk_used_notes}
                                />
                              </ListItem>
                              <Divider />
                            </>
                          )}
                        </>
                      )}

                      {/* Order Information Section */}
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom sx={{ px: 2 }}>
                        Order Information
                      </Typography>
                      {selectedOrder.notes && (
                        <>
                          <ListItem>
                            <ListItemText primary="Notes" secondary={selectedOrder.notes} />
                          </ListItem>
                          <Divider />
                        </>
                      )}
                      <ListItem>
                        <ListItemText
                          primary="Created"
                          secondary={new Date(selectedOrder.created_at).toLocaleString()}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Last Updated"
                          secondary={new Date(selectedOrder.updated_at).toLocaleString()}
                        />
                      </ListItem>
                    </List>

                    {/* Bulk Requirements Section */}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Required Bulk Products
                    </Typography>
                    {selectedOrderBulkRequirements.length > 0 ? (
                      <TableContainer component={Paper} sx={{ mt: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Bulk Product</TableCell>
                              <TableCell>SKU</TableCell>
                              <TableCell align="right">Cases Needed</TableCell>
                              {selectedOrderBulkRequirements.some(
                                (req) => req.cases_used != null
                              ) && <TableCell align="right">Cases Used</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedOrderBulkRequirements.map((req) => (
                              <TableRow key={req.bulk_product_id}>
                                <TableCell>{req.bulk_product.name}</TableCell>
                                <TableCell>{req.bulk_product.sku_number}</TableCell>
                                <TableCell align="right">{req.cases_needed}</TableCell>
                                {selectedOrderBulkRequirements.some(
                                  (r) => r.cases_used != null
                                ) && (
                                  <TableCell align="right">
                                    {req.cases_used != null ? req.cases_used : ''}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <Typography variant="body2" color="text.secondary">
                          No bulk products required for this order, or bulk requirements are still
                          loading...
                        </Typography>
                      </Paper>
                    )}
                  </>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={8}
                  >
                    <FactoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Select a Production Order
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Click on a production order from the list to view detailed information and
                      manage the order status.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Create Order Dialog */}
        <Dialog
          open={openCreateDialog}
          onClose={() => {
            if (!isCreatingOrder) {
              setOpenCreateDialog(false);
              resetForm();
              setIsCreatingOrder(false);
            }
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create Production Order</DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
                {dialogError}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Autocomplete
                  options={products}
                  loading={loadingProducts}
                  value={products.find((p) => p.id === formData.product_id) || null}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      product_id: newValue?.id || 0,
                      // Prepopulate cases with the product's ideal stock level
                      quantity_cases: newValue?.ideal_po_cases || 0,
                      // Auto-populate production line from product, but allow override
                      production_line_id: newValue?.production_line_id || undefined,
                    });
                    // Only clear search term if user deselects (newValue is null)
                    // This prevents the field from becoming blank when a product is selected
                    if (!newValue) {
                      setProductSearchTerm('');
                    }
                  }}
                  getOptionLabel={(option) => `${option.name} (${option.product_code})`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onInputChange={(_, newInputValue, reason) => {
                    // Only update search term when user is typing, not when selecting/closing/resetting
                    // This prevents infinite loops when selecting a product
                    if (reason === 'input') {
                      setProductSearchTerm(newInputValue);
                    } else if (reason === 'clear') {
                      // Clear search term when user clears the input
                      setProductSearchTerm('');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Product"
                      placeholder="Start typing to search products..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProducts ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Cases"
                  type="number"
                  value={formData.quantity_cases}
                  onChange={(e) =>
                    setFormData({ ...formData, quantity_cases: Number(e.target.value) })
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Units"
                  type="number"
                  value={formData.quantity_units}
                  onChange={(e) =>
                    setFormData({ ...formData, quantity_units: Number(e.target.value) })
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => handleDateChange('due_date', e.target.value)}
                  onBlur={(e) => handleDateBlur('due_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: '1000-01-01',
                    max: '9999-12-31',
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Production Line</InputLabel>
                  <Select
                    value={formData.production_line_id || ''}
                    label="Production Line"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        production_line_id: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>Select Production Line (defaults to product's line)</em>
                    </MenuItem>
                    {productionLines.map((line) => (
                      <MenuItem key={line.id} value={line.id}>
                        {line.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenCreateDialog(false);
                resetForm();
                setIsCreatingOrder(false);
              }}
              disabled={isCreatingOrder}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrder}
              variant="contained"
              disabled={isCreatingOrder}
              startIcon={isCreatingOrder ? <CircularProgress size={20} /> : null}
            >
              {isCreatingOrder ? 'Creating...' : 'Create Order'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Order Dialog */}
        <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
          <DialogTitle
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            Edit Production Order #{editingOrder?.production_order_id}
            <Button onClick={handleCloseEditDialog} sx={{ minWidth: 'auto', p: 1 }} color="inherit">
              <CloseIcon />
            </Button>
          </DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
                {dialogError}
              </Alert>
            )}
            {editingOrder?.status === 'completed' ? (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Actual Start Date/Time"
                    type="datetime-local"
                    value={completedOrderFormData.actual_start_time}
                    onChange={(e) =>
                      setCompletedOrderFormData({
                        ...completedOrderFormData,
                        actual_start_time: e.target.value,
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Actual End Date/Time"
                    type="datetime-local"
                    value={completedOrderFormData.actual_end_time}
                    onChange={(e) =>
                      setCompletedOrderFormData({
                        ...completedOrderFormData,
                        actual_end_time: e.target.value,
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {completedOrderFormData.actual_start_time &&
                  completedOrderFormData.actual_end_time && (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        <Typography variant="body2">
                          <strong>Total Production Time:</strong>{' '}
                          {(() => {
                            try {
                              const start = new Date(completedOrderFormData.actual_start_time);
                              const end = new Date(completedOrderFormData.actual_end_time);
                              if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                                return formatActualProductionMinutesLabel(
                                  actualProductionDurationMinutes({
                                    actual_start_time: completedOrderFormData.actual_start_time,
                                    actual_end_time: completedOrderFormData.actual_end_time,
                                    total_pause_duration_seconds: 0,
                                  })
                                );
                              }
                              return 'Invalid dates';
                            } catch {
                              return 'Invalid dates';
                            }
                          })()}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Actual Cases Produced"
                    type="number"
                    value={completedOrderFormData.actual_cases_produced}
                    onChange={(e) =>
                      setCompletedOrderFormData({
                        ...completedOrderFormData,
                        actual_cases_produced: Number(e.target.value),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Actual Units Produced"
                    type="number"
                    value={completedOrderFormData.actual_units_produced}
                    onChange={(e) =>
                      setCompletedOrderFormData({
                        ...completedOrderFormData,
                        actual_units_produced: Number(e.target.value),
                      })
                    }
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Cases"
                    type="number"
                    value={formData.quantity_cases}
                    onChange={(e) =>
                      setFormData({ ...formData, quantity_cases: Number(e.target.value) })
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Units"
                    type="number"
                    value={formData.quantity_units}
                    onChange={(e) =>
                      setFormData({ ...formData, quantity_units: Number(e.target.value) })
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Due Date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => handleDateChange('due_date', e.target.value)}
                    onBlur={(e) => handleDateBlur('due_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: '1000-01-01',
                      max: '9999-12-31',
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Production Line</InputLabel>
                    <Select
                      value={formData.production_line_id || ''}
                      label="Production Line"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          production_line_id: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                    >
                      <MenuItem value="">
                        <em>Select Production Line</em>
                      </MenuItem>
                      {productionLines.map((line) => (
                        <MenuItem key={line.id} value={line.id}>
                          {line.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button onClick={handleUpdateOrder} variant="contained">
              Update Order
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Confirmation Dialog */}
        <Dialog open={showEditConfirmation} onClose={handleCancelUpdate} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Update</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to update this production order with the new values?
            </Typography>
            {pendingEditData && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Changes to be made:
                </Typography>
                <List dense>
                  {editingOrder?.status === 'completed' ? (
                    <>
                      {pendingEditData.actual_start_time && (
                        <ListItem>
                          <ListItemText
                            primary="Actual Start Time"
                            secondary={new Date(pendingEditData.actual_start_time).toLocaleString()}
                          />
                        </ListItem>
                      )}
                      {pendingEditData.actual_end_time && (
                        <ListItem>
                          <ListItemText
                            primary="Actual End Time"
                            secondary={new Date(pendingEditData.actual_end_time).toLocaleString()}
                          />
                        </ListItem>
                      )}
                      {pendingEditData.actual_start_time && pendingEditData.actual_end_time && (
                        <ListItem>
                          <ListItemText
                            primary="Total Production Time"
                            secondary={(() => {
                              try {
                                const start = new Date(pendingEditData.actual_start_time);
                                const end = new Date(pendingEditData.actual_end_time);
                                if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                                  return formatActualProductionMinutesLabel(
                                    actualProductionDurationMinutes({
                                      actual_start_time: pendingEditData.actual_start_time,
                                      actual_end_time: pendingEditData.actual_end_time,
                                      total_pause_duration_seconds: 0,
                                    })
                                  );
                                }
                                return 'Invalid dates';
                              } catch {
                                return 'Invalid dates';
                              }
                            })()}
                          />
                        </ListItem>
                      )}
                      {pendingEditData.actual_cases_produced !== undefined && (
                        <ListItem>
                          <ListItemText
                            primary="Actual Cases Produced"
                            secondary={pendingEditData.actual_cases_produced}
                          />
                        </ListItem>
                      )}
                      {pendingEditData.actual_units_produced !== undefined && (
                        <ListItem>
                          <ListItemText
                            primary="Actual Units Produced"
                            secondary={pendingEditData.actual_units_produced}
                          />
                        </ListItem>
                      )}
                    </>
                  ) : (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Quantity"
                          secondary={`${pendingEditData.quantity_cases} cases, ${pendingEditData.quantity_units} units`}
                        />
                      </ListItem>
                      {pendingEditData.due_date && (
                        <ListItem>
                          <ListItemText
                            primary="Due Date"
                            secondary={formatDate(pendingEditData.due_date)}
                          />
                        </ListItem>
                      )}
                      {pendingEditData.priority_level && (
                        <ListItem>
                          <ListItemText
                            primary="Priority"
                            secondary={pendingEditData.priority_level.toUpperCase()}
                          />
                        </ListItem>
                      )}
                      {pendingEditData.production_line_id !== undefined && (
                        <ListItem>
                          <ListItemText
                            primary="Production Line"
                            secondary={
                              productionLines.find(
                                (l) => l.id === pendingEditData.production_line_id
                              )?.name || 'Not set'
                            }
                          />
                        </ListItem>
                      )}
                      {pendingEditData.notes && (
                        <ListItem>
                          <ListItemText primary="Notes" secondary={pendingEditData.notes} />
                        </ListItem>
                      )}
                    </>
                  )}
                </List>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelUpdate}>Cancel</Button>
            <Button onClick={handleConfirmUpdate} variant="contained" color="primary">
              Confirm Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Start Production Dialog */}
        <Dialog
          open={openStartDialog}
          onClose={() => setOpenStartDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Start Production Order #{startingOrder?.production_order_id}</DialogTitle>
          <DialogContent>
            {startingOrder && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Order Summary */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom>
                      Order Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Product
                        </Typography>
                        <Typography variant="body1">
                          {startingOrder.product_name} ({startingOrder.product_code})
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Original Quantity
                        </Typography>
                        <Typography variant="body1">
                          {formatQuantity(
                            startingOrder.quantity_cases,
                            startingOrder.quantity_units
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Override Cases */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Production Quantity
                  </Typography>
                  <TextField
                    fullWidth
                    label="Cases to Produce"
                    type="number"
                    value={startFormData.overrideCases}
                    onChange={(e) =>
                      setStartFormData({ ...startFormData, overrideCases: Number(e.target.value) })
                    }
                    helperText="Override the number of cases to produce (optional)"
                  />
                </Grid>

                {/* Bulk Requirements */}
                {bulkRequirements.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Required Bulk Products
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Bulk Product</TableCell>
                            <TableCell>SKU</TableCell>
                            <TableCell align="right">Cases Needed</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bulkRequirements.map((req) => (
                            <TableRow key={req.bulk_product_id}>
                              <TableCell>{req.bulk_product.name}</TableCell>
                              <TableCell>{req.bulk_product.sku_number}</TableCell>
                              <TableCell align="right">{req.cases_needed}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* Start Confirmation */}
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">Starting this production order will:</Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Generate a unique lot number</li>
                      <li>Record the start time automatically</li>
                      <li>Change status to "In Progress"</li>
                      {startFormData.overrideCases !== startingOrder.quantity_cases && (
                        <li>Override quantity to {startFormData.overrideCases} cases</li>
                      )}
                    </ul>
                  </Alert>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenStartDialog(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmStartProduction}
              variant="contained"
              color="success"
              startIcon={<StartIcon />}
            >
              Start Production
            </Button>
          </DialogActions>
        </Dialog>

        {/* Complete Production Dialog */}
        <Dialog
          open={openCompleteDialog}
          onClose={() => setOpenCompleteDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Complete Production Order #{completingOrder?.production_order_id}
          </DialogTitle>
          <DialogContent>
            {completingOrder && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Production Results */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Production Results
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Actual Cases Produced"
                        type="number"
                        value={completeFormData.actualCasesProduced}
                        onChange={(e) =>
                          setCompleteFormData({
                            ...completeFormData,
                            actualCasesProduced: Number(e.target.value),
                          })
                        }
                        required
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Actual Units Produced"
                        type="number"
                        value={completeFormData.actualUnitsProduced}
                        onChange={(e) =>
                          setCompleteFormData({
                            ...completeFormData,
                            actualUnitsProduced: Number(e.target.value),
                          })
                        }
                        required
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Production Issues */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Production Issues (Optional)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Issue Category</InputLabel>
                        <Select
                          value={completeFormData.issueCategory}
                          onChange={(e) =>
                            setCompleteFormData({
                              ...completeFormData,
                              issueCategory: e.target.value,
                            })
                          }
                          label="Issue Category"
                        >
                          <MenuItem value="">No Issues</MenuItem>
                          <MenuItem value="equipment">Equipment</MenuItem>
                          <MenuItem value="materials">Materials</MenuItem>
                          <MenuItem value="quality">Quality</MenuItem>
                          <MenuItem value="staffing">Staffing</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Issue Severity</InputLabel>
                        <Select
                          value={completeFormData.issueSeverity}
                          onChange={(e) =>
                            setCompleteFormData({
                              ...completeFormData,
                              issueSeverity: e.target.value as 'low' | 'medium' | 'high',
                            })
                          }
                          label="Issue Severity"
                        >
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Issue Description"
                        multiline
                        rows={3}
                        value={completeFormData.productionIssues}
                        onChange={(e) =>
                          setCompleteFormData({
                            ...completeFormData,
                            productionIssues: e.target.value,
                          })
                        }
                        placeholder="Describe any issues that occurred during production..."
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Bulk Usage */}
                {bulkUsage.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Bulk Product Usage
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Bulk Product</TableCell>
                            <TableCell>SKU</TableCell>
                            <TableCell align="right">Expected</TableCell>
                            <TableCell align="right">Actual Used</TableCell>
                            <TableCell>Notes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bulkUsage.map((usage, index) => (
                            <TableRow key={usage.bulk_product_id}>
                              <TableCell>{usage.bulk_product.name}</TableCell>
                              <TableCell>{usage.bulk_product.sku_number}</TableCell>
                              <TableCell align="right">{usage.cases_needed}</TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={usage.cases_used}
                                  onChange={(e) => {
                                    const newUsage = [...bulkUsage];
                                    newUsage[index].cases_used = Number(e.target.value);
                                    setBulkUsage(newUsage);
                                  }}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  value={usage.notes}
                                  onChange={(e) => {
                                    const newUsage = [...bulkUsage];
                                    newUsage[index].notes = e.target.value;
                                    setBulkUsage(newUsage);
                                  }}
                                  placeholder="Notes..."
                                  sx={{ width: 120 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TextField
                      fullWidth
                      label="Bulk Usage Summary Notes"
                      multiline
                      rows={2}
                      value={completeFormData.bulkUsedNotes}
                      onChange={(e) =>
                        setCompleteFormData({
                          ...completeFormData,
                          bulkUsedNotes: e.target.value,
                        })
                      }
                      placeholder="Overall notes about bulk product usage..."
                      sx={{ mt: 2 }}
                    />
                  </Grid>
                )}

                {/* Leftover Cases */}
                {completeFormData.actualCasesProduced < completingOrder.quantity_cases && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Leftover Cases
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      You produced {completeFormData.actualCasesProduced} cases but the order was
                      for {completingOrder.quantity_cases} cases. There are{' '}
                      {completingOrder.quantity_cases - completeFormData.actualCasesProduced} cases
                      remaining.
                    </Alert>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={completeFormData.createNewOrderForLeftover}
                          onChange={(e) =>
                            setCompleteFormData({
                              ...completeFormData,
                              createNewOrderForLeftover: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Create new production order for leftover cases"
                    />
                    {completeFormData.createNewOrderForLeftover && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Due Date for New Order"
                            type="date"
                            value={completeFormData.newOrderDueDate}
                            onChange={(e) =>
                              setCompleteFormData({
                                ...completeFormData,
                                newOrderDueDate: e.target.value,
                              })
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl fullWidth>
                            <InputLabel>Priority for New Order</InputLabel>
                            <Select
                              value={completeFormData.newOrderPriority}
                              onChange={(e) =>
                                setCompleteFormData({
                                  ...completeFormData,
                                  newOrderPriority: e.target.value as any,
                                })
                              }
                              label="Priority for New Order"
                            >
                              <MenuItem value="low">Low</MenuItem>
                              <MenuItem value="normal">Normal</MenuItem>
                              <MenuItem value="high">High</MenuItem>
                              <MenuItem value="urgent">Urgent</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Notes for New Order"
                            value={completeFormData.newOrderNotes}
                            onChange={(e) =>
                              setCompleteFormData({
                                ...completeFormData,
                                newOrderNotes: e.target.value,
                              })
                            }
                            placeholder="Notes for the new production order..."
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                )}

                {/* Completion Summary */}
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">Completing this production order will:</Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Record the end time automatically</li>
                      <li>Change status to "Completed"</li>
                      <li>
                        Add {completeFormData.actualCasesProduced} cases and{' '}
                        {completeFormData.actualUnitsProduced} units to inventory
                      </li>
                      <li>Record bulk product usage</li>
                      {completeFormData.createNewOrderForLeftover && (
                        <li>
                          Create a new production order for{' '}
                          {completingOrder.quantity_cases - completeFormData.actualCasesProduced}{' '}
                          leftover cases
                        </li>
                      )}
                    </ul>
                  </Alert>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCompleteDialog(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmCompleteProduction}
              variant="contained"
              color="primary"
              startIcon={<CompleteIcon />}
            >
              Complete Production
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ProductionOrders;
